import { Locale, Store } from '@moonpig/web-core-types'
import {
  IconSystemAlcohol,
  IconSystemBirthday,
  IconSystemGift,
  IconSystemChocolate,
  IconSystemMug,
  IconSystemFlowers,
  IconSystemPlussparkle as IconSystemMoonpigPlus,
  IconSystemBalloons,
  IconSystemEgift as IconSystemEGifts,
  IconSystemValentinesday as IconSystemValentinesDay,
  IconSystemMothersday as IconSystemMothersDay,
  IconSystemFathersday as IconSystemFathersDay,
  IconSystemBrand,
  IconSystemCards,
  IconSystemChristmas,
  IconSystemGroup,
  IconSystemEdit,
} from '@moonpig/launchpad-assets'
import { SVGProps } from 'react'
import {
  AppItem,
  CardFilterOption,
  ContactChannelsPerRegion,
  FooterConfig,
  InterestFacetMapping,
  PaymentMethodsPerRegion,
  ProductTabType,
  RemindersMissionButtonConfig,
} from '../types'
import { createSovendusConfig, createWebLoyaltyConfig } from '../shared'
import remindersDefaultBackground from '../../assets/moonpig/images/reminders-default-bg.jpg'
import remindersHeartsBackground from '../../assets/moonpig/images/reminders-hearts-bg.jpg'

const STORE_ID_UK = 'uk'
const STORE_ID_US = 'us'
const STORE_ID_AU = 'au'
const STORE_ID_IE = 'ie'

const LOCALE_EN_GB: Locale = {
  language: 'en-GB',
  countryCode: 'gb',
  currencyCode: 'gbp',
  timeZone: 'Europe/London',
}

const LOCALE_EN_US: Locale = {
  language: 'en-US',
  countryCode: 'us',
  currencyCode: 'usd',
  timeZone: 'America/New_York',
}

const LOCALE_EN_AU: Locale = {
  language: 'en-AU',
  countryCode: 'au',
  currencyCode: 'aud',
  timeZone: 'Australia/Sydney',
}

const LOCALE_EN_IE: Locale = {
  language: 'en-IE',
  countryCode: 'ie',
  currencyCode: 'eur',
  timeZone: 'Europe/Dublin',
}

const GTM_CLIENT = {
  containerId: 'GTM-M8RZDQ9',
  verificationCode: '6ynlpPuN0ok0-Oy8NELN7XktiFH-5iVAaoB7uIAdTUA',
  host: 'www.googletagmanager.com',
  stapeContainerId:
    'DwxaMDMiRSdeOj0zNlZOHFtaSUZBCwZXER8QWQoABwQWAQBZBAAFTBUHF0pIAgcFCAQIEA%3D%3D%3D',
  stapeJsFilename: '7z9ujhogwghf.js',
}

export const STORES: Store[] = [
  {
    id: STORE_ID_UK,
    legacyId: 'mnpg-uk',
    shortName: 'UK',
    name: 'United Kingdom',
    locale: LOCALE_EN_GB,
    analytics: {
      gtmClient: GTM_CLIENT,
    },
    webLoyalty: createWebLoyaltyConfig(STORE_ID_UK),
    sovendus: createSovendusConfig(STORE_ID_UK),
    supportsWork: true,
  },
  {
    id: STORE_ID_US,
    legacyId: 'mnpg-us',
    shortName: 'US & CA',
    name: 'United States & Canada',
    locale: LOCALE_EN_US,
    analytics: {
      gtmClient: GTM_CLIENT,
    },
    webLoyalty: createWebLoyaltyConfig(STORE_ID_US),
    supportsWork: false,
  },
  {
    id: STORE_ID_AU,
    legacyId: 'mnpg-au',
    shortName: 'AU & NZ',
    name: 'Australia & New Zealand',
    locale: LOCALE_EN_AU,
    analytics: {
      gtmClient: GTM_CLIENT,
    },
    webLoyalty: createWebLoyaltyConfig(STORE_ID_AU),
    supportsWork: false,
  },
  {
    id: STORE_ID_IE,
    legacyId: 'mnpg-ie',
    shortName: 'Ireland',
    name: 'Ireland',
    locale: LOCALE_EN_IE,
    analytics: {
      gtmClient: GTM_CLIENT,
    },
    webLoyalty: createWebLoyaltyConfig(STORE_ID_IE),
    supportsWork: false,
  },
]

const API_ENV = process.env.MNPG_ENVIRONMENT_NAME as string
const API_REGIONS = ['eu-west-1', 'us-east-1', 'ap-southeast-2']

const GOOGLE_AUTH_CLIENT_IDS: { [key: string]: string } = {
  local:
    '1039387418628-aeaadab0b9scdco8k0va17tq6fdpmt3e.apps.googleusercontent.com',
  dev: '207178689975-bggvued3vl0690un97015g3eer3raefv.apps.googleusercontent.com',
  uat: '1039387418628-aeaadab0b9scdco8k0va17tq6fdpmt3e.apps.googleusercontent.com',
  prod: '1088325004894-58r2stpbdfrm835inslp5f903vmvhq2l.apps.googleusercontent.com',
}

export const GOOGLE_AUTH_CLIENT_ID: string | undefined =
  GOOGLE_AUTH_CLIENT_IDS[API_ENV]

const MEDIA_API_HOST_NAMES = [
  // default region (without amazonaws.com subdomain)
  `mnpg-media-assets-us-east-1-${API_ENV}-uploaded.s3.amazonaws.com`,
  `mnpg-media-assets-us-east-1-${API_ENV}-converted.s3.amazonaws.com`,
  // other regions
  ...API_REGIONS.map(
    region =>
      `mnpg-media-assets-${region}-${API_ENV}-uploaded.s3.${region}.amazonaws.com`,
  ),
  ...API_REGIONS.map(
    region =>
      `mnpg-media-assets-${region}-${API_ENV}-converted.s3.${region}.amazonaws.com`,
  ),
]

const CUSTOMER_CREATIONS_API_HOST_NAMES = [
  // default region (without amazonaws.com subdomain)
  `mnpg-customer-creations-storage-us-east-1-${API_ENV}-assets.s3.amazonaws.com`,
  // other regions
  ...API_REGIONS.map(
    region =>
      `mnpg-customer-creations-storage-${region}-${API_ENV}-assets.s3.${region}.amazonaws.com`,
  ),
]

const DESIGN_API_HOST_NAMES = [
  // default region (without amazonaws.com subdomain)
  `mnpg-design-api-us-east-1-${API_ENV}-image-upload.s3.amazonaws.com`,
  // other regions
  ...API_REGIONS.flatMap(region => [
    `mnpg-design-api-${region}-${API_ENV}-image-upload.s3.${region}.amazonaws.com`,
    `mnpg-design-sticker-ai-generator-${region}-${API_ENV}-store.s3.${region}.amazonaws.com`,
  ]),
]

const DESIGN_AI_CREATIONS_HOST_NAMES = [
  // default region (without amazonaws.com subdomain)
  `mnpg-design-ai-creations-storage-us-east-1-${API_ENV}-assets.s3.amazonaws.com`,
  // other regions
  ...API_REGIONS.flatMap(region => [
    `mnpg-design-ai-creations-storage-${region}-${API_ENV}-assets.s3.${region}.amazonaws.com`,
  ]),
]

export const contentSecurityPolicy: { [key: string]: string[] } = {
  'font-src': [
    '*.moonpig.com',
    '*.moonpig.net',
    ...CUSTOMER_CREATIONS_API_HOST_NAMES,
  ],
  'connect-src': [
    ...CUSTOMER_CREATIONS_API_HOST_NAMES,
    ...DESIGN_AI_CREATIONS_HOST_NAMES,
    ...DESIGN_API_HOST_NAMES,
    ...MEDIA_API_HOST_NAMES,
    '*.lunio.ai',
    '*.moonpig.com',
    '*.moonpig.net',
    '*.sail-horizon.com',
    '*.sail-personalize.com',
    '*.sail-track.com',
  ],
  'frame-src': [],
  'style-src': ['*.moonpig.com', '*.moonpig.net'],
  'script-src': [
    '*.moonpig.com',
    '*.moonpig.net',
    '*.sail-horizon.com',
    '*.sail-personalize.com',
    '*.sail-track.com',
  ],
  'img-src': [],
  'media-src': [
    ...MEDIA_API_HOST_NAMES,
    '*.moonpig.net',
    'videos.ctfassets.net',
  ],
}

export const ONE_TRUST_DOMAIN_SCRIPT_ID = 'e46f8143-8f58-4c40-a105-e42e91ce027b'

export const BRAND_LABEL = 'Moonpig'

export const BRAND_LABEL_WITH_LOCATION: Record<string, string> = {
  [STORE_ID_UK]: 'Moonpig',
  [STORE_ID_US]: 'Moonpig USA',
  [STORE_ID_AU]: 'Moonpig Australia',
  [STORE_ID_IE]: 'Moonpig Ireland',
}

export const REMINDERS_MISSION_BUTTON_DEFAULT_BACKGROUND =
  remindersDefaultBackground

// galleryPath gets `storeId` added within the consuming component.
export const REMINDERS_MISSION_BUTTON_CONFIG: RemindersMissionButtonConfig = {
  VALENTINES_DAY: {
    localeKey: 'cvr.reminders_mission_buttons.VALENTINES_DAY.name',
    fallbackLocaleKey: 'cvr.reminders_mission_buttons.VALENTINES_DAY.fallback',
    galleryPath: 'valentines-day',
    backgroundImage: remindersHeartsBackground,
  },
  MOTHERS_DAY: {
    localeKey: 'cvr.reminders_mission_buttons.MOTHERS_DAY.name',
    fallbackLocaleKey: 'cvr.reminders_mission_buttons.MOTHERS_DAY.fallback',
    galleryPath: 'mothers-day',
    backgroundImage: remindersHeartsBackground,
  },
  BIRTHDAY: {
    localeKey: 'cvr.reminders_mission_buttons.BIRTHDAY.name',
    fallbackLocaleKey: 'cvr.reminders_mission_buttons.BIRTHDAY.fallback',
  },
  ANNIVERSARY: {
    localeKey: 'cvr.reminders_mission_buttons.ANNIVERSARY.name',
    fallbackLocaleKey: 'cvr.reminders_mission_buttons.ANNIVERSARY.fallback',
    backgroundImage: remindersHeartsBackground,
  },
}

export const DEPARTMENT_PATHS = {
  gifts: 'gifts',
  cards: 'cards',
  'flowers-and-plants': 'flowers-and-plants',
}

export const APP_STORE_ITEMS: AppItem[] = [
  {
    id: 'apple',
    icon: 'apple-app-store',
    to: 'https://itunes.apple.com/app/id393730279',
    width: '120px',
    height: '40px',
  },
  {
    id: 'google',
    icon: 'google-app-store',
    to: 'https://play.google.com/store/apps/details?id=com.commonagency.moonpig.uk',
    width: '135px',
    height: '40px',
  },
]

export const PAYMENT_METHODS: PaymentMethodsPerRegion = {
  uk: ['Visa', 'Mastercard', 'Google Pay', 'Apple Pay', 'PayPal'],
  us: ['Visa', 'Mastercard', 'Google Pay', 'Apple Pay', 'PayPal'],
  au: ['Visa', 'Mastercard', 'Google Pay', 'Apple Pay', 'PayPal'],
  ie: ['Visa', 'Mastercard', 'Google Pay', 'Apple Pay', 'PayPal'],
}

export const FOOTER: FooterConfig = {
  socialItems: {
    uk: [
      {
        icon: 'facebook',
        linkHref: 'https://www.facebook.com/Moonpig/',
        label: 'Facebook',
      },
      {
        icon: 'twitter',
        linkHref: 'https://twitter.com/moonpiguk',
        label: 'Twitter',
      },
      {
        icon: 'instagram',
        linkHref: 'https://www.instagram.com/moonpig/',
        label: 'Instagram',
      },
      {
        icon: 'pinterest',
        linkHref: 'https://www.pinterest.co.uk/moonpiggroup/',
        label: 'Pinterest',
      },
      {
        icon: 'youtube',
        linkHref: 'https://www.youtube.com/user/moonpigcards',
        label: 'YouTube',
      },
    ],
    us: [
      {
        icon: 'facebook',
        linkHref: 'https://www.facebook.com/moonpigusa/',
        label: 'Facebook',
      },
      {
        icon: 'instagram',
        linkHref: 'https://www.instagram.com/moonpigusa/',
        label: 'Instagram',
      },
    ],
    au: [
      {
        icon: 'facebook',
        linkHref: 'https://www.facebook.com/MoonpigAustralia',
        label: 'Facebook',
      },
      {
        icon: 'instagram',
        linkHref: 'https://www.instagram.com/moonpigau/',
        label: 'Instagram',
      },
      {
        icon: 'tiktok',
        linkHref: 'https://www.tiktok.com/@moonpig.au?_t=ZN-8zOF4klbuJK&_r=1',
        label: 'TikTok',
      },
    ],
    ie: [
      {
        icon: 'facebook',
        linkHref: 'https://www.facebook.com/moonpigie',
        label: 'Facebook',
      },
      {
        icon: 'instagram',
        linkHref: 'https://www.instagram.com/moonpigie/',
        label: 'Instagram',
      },
    ],
  },
  appItems: APP_STORE_ITEMS,
  regionItems: [
    {
      region: 'uk',
      linkHref: '/uk/',
      shortLabel: 'UK',
      label: 'United Kingdom',
      icon: 'uk-flag',
    },
    {
      region: 'us',
      linkHref: '/us/',
      shortLabel: 'US',
      label: 'United States of America',
      icon: 'us-flag',
    },
    {
      region: 'au',
      linkHref: '/au/',
      shortLabel: 'AUS',
      label: 'Australia',
      icon: 'aus-flag',
    },
    {
      region: 'ie',
      linkHref: '/ie/',
      shortLabel: 'IE',
      label: 'Ireland',
      icon: 'ie-flag',
    },
  ],
  paymentProviders: PAYMENT_METHODS,
}

export const TRUSTPILOT_BUSINESS_UNIT_ID = '4bdecbe5000064000506bce0'

export const interestFacetsMappings: InterestFacetMapping[] = [
  {
    interestId: 'beer_fan',
    interestName: 'Beer Fan',
    facetKeys: ['beer-cider'],
    includeRecipient: false,
  },
  {
    interestId: 'wine_lover',
    interestName: 'Wine Lover',
    facetKeys: ['champagne-prosecco-wine'],
    includeRecipient: true,
  },
  {
    interestId: 'gin_and_spirits_fan',
    interestName: 'Gin and Spirits Fan',
    facetKeys: ['gin', 'vodka', 'other-spirits', 'whiskey'],
    includeRecipient: true,
  },
  {
    interestId: 'foodie',
    interestName: 'Foodie',
    facetKeys: [
      'snacks-treats-savoury',
      'gift-experiences-food-drink',
      'biscuits',
    ],
    priceKeys: [
      'price-£10orless',
      'price-£10-£20',
      'price-£20-£30',
      'price-£30-£40',
    ],
    includeRecipient: true,
  },
  {
    interestId: 'sweet_tooth',
    interestName: 'Sweet Tooth',
    facetKeys: ['chocolate', 'biscuits', 'sweets'],
    includeRecipient: true,
  },
  {
    interestId: 'wellness_enthusiast',
    interestName: 'Wellness Enthusiast',
    facetKeys: ['spa-breaks', 'beauty-face-body-gift-sets-letterbox'],
    includeRecipient: true,
  },
  {
    interestId: 'game_and_puzzle_solver',
    interestName: 'Game and Puzzle Solver',
    facetKeys: [
      'games-gadgets-novelty-games-board-games-puzzles',
      'gadgets',
      'games-gadgets-novelty-lego-sets',
      'games-gadgets-novelty-novelty',
    ],
    includeRecipient: true,
  },
  {
    interestId: 'jewellery_lover',
    interestName: 'Jewellery Lover',
    facetKeys: ['jewellery'],
    includeRecipient: false,
  },
  {
    interestId: 'home_stylist',
    interestName: 'Home Stylist',
    facetKeys: [
      'towels',
      'posters-prints',
      'photo-frames',
      'home-accessories',
      'glassware',
      'aprons',
    ],
    includeRecipient: true,
  },
  {
    interestId: 'gardener',
    interestName: 'Gardener',
    facetKeys: ['garden', 'grow-kits'],
    includeRecipient: true,
  },
  {
    interestId: 'adventurous',
    interestName: 'Adventurous',
    facetKeys: ['action-and-adventure', 'day-trips'],
    includeRecipient: true,
  },
  {
    interestId: 'holidayer',
    interestName: 'Holidayer',
    facetKeys: ['short-breaks', 'multi-choice'],
    includeRecipient: false,
  },
  {
    interestId: 'experience_fan',
    interestName: 'Experience Fan',
    facetKeys: [
      'product-type-gift-experiences',
      'product-type-digital-gift-experience',
    ],
    includeRecipient: true,
  },
  {
    interestId: 'flower_enthusiast',
    interestName: 'Flower Enthusiast',
    facetKeys: [
      'alstroemeria',
      'bouquets',
      'carnations',
      'dried-flowers',
      'germini',
      'lilies',
      'lisianthus',
      'roses',
      'stocks',
      'sunflowers',
      'tulips',
    ],
    includeRecipient: false,
  },
  {
    interestId: 'plant_lover',
    interestName: 'Plant Lover',
    facetKeys: ['indoor-plants', 'outdoor-plants'],
    includeRecipient: true,
  },
]

const CARD_FILTER_OCCASIONS: CardFilterOption[] = [
  {
    optionId: 'occasion-birthday',
    optionName: 'Birthday',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'anniversaries',
    optionName: 'Anniversary',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-thank-you',
    optionName: 'Thank You',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-thinking-of-you',
    optionName: 'Thinking of You',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-new-baby',
    optionName: 'New Baby',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-wedding',
    optionName: 'Wedding',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-new-home',
    optionName: 'New Home',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-sympathy',
    optionName: 'Sympathy',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-get-well',
    optionName: 'Get Well',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-congratulations',
    optionName: 'Congratulations',
    category: 'occasion',
    isSelected: false,
  },
  {
    optionId: 'occasion-leaving',
    optionName: 'Leaving',
    category: 'occasion',
    isSelected: false,
  },
]

const CARD_FILTER_RECIPIENTS: CardFilterOption[] = [
  {
    optionId: 'for-her',
    optionName: 'For Her',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'for-him',
    optionName: 'For Him',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-mum',
    optionName: 'Mum',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-dad',
    optionName: 'Dad',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'partner',
    optionName: 'Partner',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-wife',
    optionName: 'Wife',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-husband',
    optionName: 'Husband',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'grandma',
    optionName: 'Grandma',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-grandad',
    optionName: 'Grandad',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-daughter',
    optionName: 'Daughter',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-son',
    optionName: 'Son',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-sister',
    optionName: 'Sister',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'whos-it-for-brother',
    optionName: 'Brother',
    category: 'recipient',
    isSelected: false,
  },
  {
    optionId: 'friend',
    optionName: 'Friend',
    category: 'recipient',
    isSelected: false,
  },
]

const CARD_FILTER_SENTIMENT: CardFilterOption[] = [
  {
    optionId: 'sentiment-style-funny',
    optionName: 'Funny',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'cheeky',
    optionName: 'Cheeky',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'rude',
    optionName: 'Rude',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'sentiment-style-cute',
    optionName: 'Cute',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'sentiment-style-traditional',
    optionName: 'Traditional',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'sentiment-style-love',
    optionName: 'Love',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'artistic',
    optionName: 'Artistic',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'across-the-miles',
    optionName: 'Across the Miles',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'sentiment-style-floral',
    optionName: 'Floral',
    category: 'sentiment',
    isSelected: false,
  },
  {
    optionId: 'pun',
    optionName: 'Pun',
    category: 'sentiment',
    isSelected: false,
  },
]

export const CARD_FILTER_OPTIONS: CardFilterOption[] = [
  ...CARD_FILTER_OCCASIONS,
  ...CARD_FILTER_RECIPIENTS,
  ...CARD_FILTER_SENTIMENT,
]

export const GIFT_UPSELL_PRODUCT_IDS: {
  originalId: string
  upsellId: string
}[] = [
  { originalId: 'choc1835', upsellId: 'choc1836' },
  { originalId: 'choc1836', upsellId: 'choc1841' },
  { originalId: 'choc1841', upsellId: 'choc1845' },
  { originalId: 'choc1856', upsellId: 'choc1857' },
  { originalId: 'choc1857', upsellId: 'choc1862' },
  { originalId: 'choc1862', upsellId: 'choc1864' },
  { originalId: 'choc1839', upsellId: 'choc1843' },
  { originalId: 'choc1840', upsellId: 'choc1844' },
  { originalId: 'choc1844', upsellId: 'choc1846' },
  { originalId: 'choc1842', upsellId: 'choc1847' },
  { originalId: 'choc1256', upsellId: 'choc1703' },
  { originalId: 'choc1703', upsellId: 'choc1261' },
  { originalId: 'choc1257', upsellId: 'choc1707' },
  { originalId: 'choc1707', upsellId: 'choc1262' },
  { originalId: 'choc1258', upsellId: 'choc1705' },
  { originalId: 'choc1705', upsellId: 'choc1263' },
  { originalId: 'choc1267', upsellId: 'choc1635' },
  { originalId: 'choc5503', upsellId: 'choc537' },
  { originalId: 'alc827', upsellId: 'alc954' },
  { originalId: 'alc669', upsellId: 'alc709' },
  { originalId: 'alc668', upsellId: 'alc671' },
  { originalId: 'hamp697', upsellId: 'hamp698' },
  { originalId: 'hamp698', upsellId: 'hamp699' },
  { originalId: 'hamp699', upsellId: 'hamp700' },
  { originalId: 'hamp671', upsellId: 'hamp673' },
  { originalId: 'hamp674', upsellId: 'hamp684' },
  { originalId: 'hamp710', upsellId: 'hamp582' },
  { originalId: 'hamp669', upsellId: 'hamp734' },
  { originalId: 'hamp735', upsellId: 'hamp737' },
  { originalId: 'hamp734', upsellId: 'hamp737' },
  { originalId: 'hamp571', upsellId: 'hamp736' },
  { originalId: 'hamp582', upsellId: 'hamp324' },
  { originalId: 'choc1732', upsellId: 'hamp655' },
  { originalId: 'choc1808', upsellId: 'hamp655' },
  { originalId: 'choc1698', upsellId: 'hamp655' },
  { originalId: 'choc1731', upsellId: 'hamp655' },
  { originalId: 'hamp682', upsellId: 'hamp651' },
  { originalId: 'hamp678', upsellId: 'hamp651' },
  { originalId: 'choc1260', upsellId: 'choc1704' },
  { originalId: 'choc1594', upsellId: 'choc1765' },
  { originalId: 'choc1615', upsellId: 'choc1710' },
  { originalId: 'choc1263', upsellId: 'alc304b' },
  { originalId: 'choc1523', upsellId: 'alc375b' },
  { originalId: 'choc1706', upsellId: 'choc1264' },
  { originalId: 'choc1264', upsellId: 'alc419b' },
  { originalId: 'choc1576', upsellId: 'choc1089b' },
  { originalId: 'choc1621', upsellId: 'choc1117b' },
  { originalId: 'choc1690', upsellId: 'choc1182b' },
  { originalId: 'choc1648', upsellId: 'choc1187b' },
  { originalId: 'choc1526', upsellId: 'alc402b' },
  { originalId: 'choc537', upsellId: 'bal121b' },
  { originalId: 'swee790', upsellId: 'alc467b' },
  { originalId: 'choc1422', upsellId: 'alc409b' },
  { originalId: 'choc1519', upsellId: 'alc379b' },
  { originalId: 'choc851', upsellId: 'sft168b' },
  { originalId: 'bal496', upsellId: 'bal293b' },
  { originalId: 'bal428', upsellId: 'bal343b' },
  { originalId: 'ar407537', upsellId: 'bal119b' },
  { originalId: 'bal120', upsellId: 'bal121b' },
  { originalId: 'bal100', upsellId: 'bal138b' },
  { originalId: 'bal167', upsellId: 'bal136b' },
  { originalId: 'bal149', upsellId: 'bal137b' },
  { originalId: 'bal466', upsellId: 'bal345b' },
  { originalId: 'bal467', upsellId: 'bal346b' },
  { originalId: 'bal468', upsellId: 'bal347b' },
  { originalId: 'bal469', upsellId: 'bal348b' },
  { originalId: 'bal470', upsellId: 'bal349b' },
  { originalId: 'bal471', upsellId: 'bal350b' },
  { originalId: 'bal472', upsellId: 'bal351b' },
  { originalId: 'bal473', upsellId: 'bal352b' },
  { originalId: 'bal474', upsellId: 'bal353b' },
  { originalId: 'bal415', upsellId: 'bal340b' },
  { originalId: 'bal464', upsellId: 'bal341b' },
  { originalId: 'bal497', upsellId: 'bal289b' },
  { originalId: 'alc250', upsellId: 'choc1097b' },
  { originalId: 'alc360', upsellId: 'hamp672' },
  { originalId: 'alc830', upsellId: 'choc1159b' },
  { originalId: 'alc518', upsellId: 'alc1053' },
  { originalId: 'alc519', upsellId: 'hamp674' },
  { originalId: 'alc559', upsellId: 'hamp668' },
  { originalId: 'alc552', upsellId: 'alc1076' },
  { originalId: 'beer456', upsellId: 'beer470' },
  { originalId: 'alc875', upsellId: 'alc915' },
  { originalId: 'alc659', upsellId: 'alc862' },
  { originalId: 'alc998', upsellId: 'hamp683' },
  { originalId: 'alc760', upsellId: 'alc391b' },
  { originalId: 'beer490', upsellId: 'beer496' },
  { originalId: 'home1140', upsellId: 'home587b' },
  { originalId: 'home1206', upsellId: 'home584b' },
  { originalId: 'beau1800', upsellId: 'beau1666' },
  { originalId: 'beau1626', upsellId: 'beau637b' },
  { originalId: 'beau1628', upsellId: 'beau636b' },
  { originalId: 'beau1627', upsellId: 'beau638b' },
  { originalId: 'beau844', upsellId: 'beau650b' },
  { originalId: 'beau672', upsellId: 'beau1722' },
  { originalId: 'beau1623', upsellId: 'beau1624' },
  { originalId: 'beau1624', upsellId: 'beau1625' },
  { originalId: 'beau1709', upsellId: 'beau642b' },
  { originalId: 'beau1701', upsellId: 'beau1705' },
  { originalId: 'beau1707', upsellId: 'beau1706' },
  { originalId: 'beau1805', upsellId: 'beau1804' },
  { originalId: 'beau1764', upsellId: 'beau1272' },
  { originalId: 'beau1765', upsellId: 'beau1271' },
  { originalId: 'beau707', upsellId: 'beau592b' },
  { originalId: 'beau1269', upsellId: 'beau1270' },
  { originalId: 'beau972', upsellId: 'beau1634' },
  { originalId: 'beau1748', upsellId: 'beau1750' },
  { originalId: 'beau1591', upsellId: 'beau1598' },
  { originalId: 'beau1593', upsellId: 'beau640b' },
  { originalId: 'beau1557', upsellId: 'beau654b' },
  { originalId: 'home979', upsellId: 'beau654b' },
  { originalId: 'home978', upsellId: 'beau652b' },
  { originalId: 'beau1792', upsellId: 'beau1537' },
]

// SKUs whose PDP/modal should show the plants-specific USP content.
export const PLANTS_USP_SKUS: string[] = [
  'ar422462',
  'ar427229',
  'ar427224',
  'ar427226',
  'ar422846',
  'ar427051',
  'ar427799',
  'ar427797',
  'ar426871',
  'ar427057',
  'ar427228',
  'ar427798',
  'ar427053',
  'ar427223',
  'ar427227',
  'ar427231',
  'ar426610',
  'ar427800',
  'ar427059',
  'ar427793',
  'ar427052',
  'ar426609',
  'ar426608',
  'ar427058',
  'ar427230',
  'ar425602',
  'ar427040',
  'ar427060',
  'ar427043',
  'ar427225',
  'ar427047',
  'ar427045',
]

export const MULTI_CARD_LEARN_MORE_LINK = '/personalised-cards/multibuy/'

export const MULTI_CARD_CSV_LINK: { [key: string]: string } = {
  uk: `https://www.moonpig.com/static/customer/${process.env.APP_VERSION}/_next/static/csv/example_uk.csv`,
  us: `https://www.moonpig.com/static/customer/${process.env.APP_VERSION}/_next/static/csv/example_us.csv`,
  au: `https://www.moonpig.com/static/customer/${process.env.APP_VERSION}/_next/static/csv/example_au.csv`,
  ie: `https://www.moonpig.com/static/customer/${process.env.APP_VERSION}/_next/static/csv/example_ie.csv`,
}

export const MULTI_CARD_DISCOUNTS: {
  [key: string]: {
    label: string
    minimumQuantity: number
    discount: number
    code: string
  }[]
} = {
  uk: [],
  us: [
    { label: '2 Cards', minimumQuantity: 2, discount: 20, code: 'MULTISAVE' },
    { label: '3 Cards', minimumQuantity: 3, discount: 30, code: 'MULTISAVE' },
    { label: '4 Cards', minimumQuantity: 4, discount: 40, code: 'MULTISAVE' },
    { label: '5+ Cards', minimumQuantity: 5, discount: 50, code: 'MULTISAVE' },
  ],
  au: [
    { label: '2 Cards', minimumQuantity: 2, discount: 20, code: 'MULTISAVE' },
    { label: '3 Cards', minimumQuantity: 3, discount: 30, code: 'MULTISAVE' },
    { label: '4 Cards', minimumQuantity: 4, discount: 40, code: 'MULTISAVE' },
    { label: '5+ Cards', minimumQuantity: 5, discount: 50, code: 'MULTISAVE' },
  ],
  ie: [
    { label: '2 Cards', minimumQuantity: 2, discount: 20, code: 'MULTISAVE' },
    { label: '3 Cards', minimumQuantity: 3, discount: 30, code: 'MULTISAVE' },
    { label: '4 Cards', minimumQuantity: 4, discount: 40, code: 'MULTISAVE' },
    { label: '5+ Cards', minimumQuantity: 5, discount: 50, code: 'MULTISAVE' },
  ],
}

export const SHOW_MULTI_CARD_DISCOUNTS_TAG = true

export const NAVIGATION_ICONS: {
  [key: string]: (props: SVGProps<SVGSVGElement>) => React.JSX.Element
} = {
  IconSystemChristmas,
  IconSystemBirthday,
  IconSystemCards,
  IconSystemFlowers,
  IconSystemGift,
  IconSystemChocolate,
  IconSystemMug,
  IconSystemMoonpigPlus,
  IconSystemBalloons,
  IconSystemEGifts,
  IconSystemValentinesDay,
  IconSystemMothersDay,
  IconSystemFathersDay,
  IconSystemBrand,
  IconSystemAlcohol,
  IconSystemGroup,
  IconSystemEdit,
}

export const SHOW_PLUS_HEADER_RIGHT_SIDE_IMAGE = true

export const PRODUCT_TAB_TYPE: ProductTabType = 'GROUP_CARD'

export { CARD_VARIANT_IDS_ENABLED } from './product'
export { POSTCARD_UPSELL_EXCLUDED_SKUS } from './postcard'

export const PERSISTED_FILTERS: { [key: string]: string } = {
  'not-rude': 'mnpg_exclude_rude_products',
}

export const CONTACT_CHANNELS: ContactChannelsPerRegion = {
  uk: ['email', 'sms', 'whatsapp'],
  us: ['email', 'sms', 'whatsapp'],
  au: ['email', 'sms', 'whatsapp'],
  ie: ['email', 'sms', 'whatsapp'],
}

export const AGE_INTEGER_FILTER_FEATURE_ENTRY_POINT_BANNER_IMAGES = {
  xs: 'https://images.ctfassets.net/3m6f3dx67i1j/1IKS4XGbTG6bbCq0BjAnTk/922e6839234437f1394d1a62d396617c/mpg_mobile_2x.png',
  md: 'https://images.ctfassets.net/3m6f3dx67i1j/3erLmTs0EvUCeR4JZ3rkfs/759ae47c8720f203e2856b50d5424a40/mpg_desktop_2x.png',
}

export const SEARCH_NO_RESULTS_FOUND_ILLUSTRATION =
  'https://images.ctfassets.net/3m6f3dx67i1j/Xml1P3hhXQUn0gIUo8ybi/4f4a2c05a1b1ba561aaf6e372d90300b/no_results_moonpig.svg'

export const MISSION_BUTTONS_CAROUSEL_THRESHOLD = 8
